import { useState } from 'react';
import './password.css';
import lockShieldImg from '../assets/icon.png';

function ChevronLeftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M15 5l-7 7 7 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <rect x="5" y="11" width="14" height="10" rx="2.5" stroke="#8a6df1" strokeWidth="1.8" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="#8a6df1" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="12" cy="16" r="1.6" fill="#8a6df1" />
    </svg>
  );
}

function EyeIcon({ open }) {
  if (open) {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path
          d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
      </svg>
    );
  }
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path
        d="M3 3l18 18M10.6 10.7a3 3 0 0 0 4.2 4.2M6.6 6.7C4 8.4 2 12 2 12s4 7 11 7c1.6 0 3-.3 4.3-.9M9.9 5.1A11.5 11.5 0 0 1 12 5c7 0 11 7 11 7-.5.9-1.3 2-2.4 3"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckCircleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8.5 12.2l2.3 2.3 4.7-4.7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const RULES = [
  { key: 'length', label: 'At least 8 characters', test: (v) => v.length >= 8 },
  { key: 'upper', label: 'One uppercase letter (A-Z)', test: (v) => /[A-Z]/.test(v) },
  { key: 'lower', label: 'One lowercase letter (a-z)', test: (v) => /[a-z]/.test(v) },
  { key: 'number', label: 'One number (0-9)', test: (v) => /[0-9]/.test(v) },
  { key: 'special', label: 'One special character (e.g. !@#$%)', test: (v) => /[^A-Za-z0-9]/.test(v) },
];

export default function PasswordStep({ onBack, onContinue }) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const allRulesMet = RULES.every((rule) => rule.test(password));
  const passwordsMatch = confirmPassword.length > 0 && password === confirmPassword;
  const isValid = allRulesMet && passwordsMatch;

  return (
    <div className="password-screen">
      <div className="password-frame">
        <div className="password-header">
          <button type="button" className="password-back-btn" onClick={onBack} aria-label="Go back">
            <ChevronLeftIcon />
          </button>

          <div className="password-glow" />
          <div className="password-icon-wrap">
            <img src={lockShieldImg} alt="" className="password-icon-img" />
          </div>
          <h1 className="password-title">Create a password</h1>
          <p className="password-subtitle">Make sure it&apos;s strong and secure</p>
        </div>

        <form
          className="password-card"
          onSubmit={(e) => {
            e.preventDefault();
            if (isValid) onContinue?.({ password });
          }}
        >
          <div className="password-field">
            <label className="password-label" htmlFor="password">
              Password
            </label>
            <div className="password-input-wrap">
              <LockIcon />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="password-eye-btn"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <EyeIcon open={showPassword} />
              </button>
            </div>
          </div>

          <div className="password-field">
            <label className="password-label" htmlFor="confirm-password">
              Confirm Password
            </label>
            <div className="password-input-wrap">
              <LockIcon />
              <input
                id="confirm-password"
                type={showConfirm ? 'text' : 'password'}
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <button
                type="button"
                className="password-eye-btn"
                onClick={() => setShowConfirm((v) => !v)}
                aria-label={showConfirm ? 'Hide password' : 'Show password'}
              >
                <EyeIcon open={showConfirm} />
              </button>
            </div>
          </div>

          <div className="password-requirements">
            <p className="password-requirements-title">Password must contain:</p>
            {RULES.map((rule) => {
              const met = rule.test(password);
              return (
                <div className={`password-rule ${met ? 'met' : ''}`} key={rule.key}>
                  <CheckCircleIcon />
                  {rule.label}
                </div>
              );
            })}
          </div>

          <button type="submit" className="password-submit" disabled={!isValid}>
            Continue <ArrowRightIcon />
          </button>
        </form>
      </div>
    </div>
  );
}
